/**
 * Google Drive Helper Utilities
 * Provides functions for extracting file IDs, generating thumbnails, and handling Drive URLs
 */

export interface DriveFileInfo {
  fileId: string | null;
  thumbnail: string | null;
  mimeType: string | null;
}

/**
 * Extract Google Drive file ID from various URL formats
 */
export const extractDriveFileId = (url: string): string | null => {
  if (!url) return null;
  
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
    /\/d\/([a-zA-Z0-9_-]+)/,
    /gdrive::([a-zA-Z0-9_-]+)/ // New format: gdrive::FILE_ID::THUMBNAIL::MIME
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
};

/**
 * Parse Google Drive URL to extract file info
 */
export const parseGDriveUrl = (url: string): DriveFileInfo => {
  // Check if it's the new format: gdrive::FILE_ID::THUMBNAIL_URL::MIME_TYPE
  const newFormatMatch = url.match(/gdrive::([^:]+)::([^:]+)::(.+)/);
  if (newFormatMatch?.[1] && newFormatMatch[2] && newFormatMatch[3]) {
    return {
      fileId: newFormatMatch[1],
      thumbnail: decodeURIComponent(newFormatMatch[2]),
      mimeType: newFormatMatch[3]
    };
  }
  
  // Fallback to old format
  const fileId = extractDriveFileId(url);
  return {
    fileId,
    thumbnail: fileId ? `https://drive.google.com/thumbnail?id=${fileId}&sz=w400` : null,
    mimeType: null
  };
};

/**
 * Check if URL is a video
 */
export const isVideoUrl = (url: string): boolean => {
  if (url.startsWith('video::')) return true;
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

/**
 * Get thumbnail URL for Google Drive file
 */
export const getDriveThumbnailUrl = (fileIdOrUrl: string, size: 'small' | 'medium' | 'large' = 'medium'): string | null => {
  const fileId = extractDriveFileId(fileIdOrUrl) || fileIdOrUrl;
  if (!fileId) return null;
  
  const sizeMap = {
    small: 'w200',
    medium: 'w400',
    large: 'w800'
  };
  
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=${sizeMap[size]}`;
};

/**
 * Get streaming URL for Google Drive file through edge function
 */
export const getDriveStreamUrl = (fileIdOrUrl: string): string | null => {
  const fileId = extractDriveFileId(fileIdOrUrl) || fileIdOrUrl;
  if (!fileId) return null;
  
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/functions/v1/google-drive-stream?id=${fileId}`;
};

/**
 * Get Google Drive preview/player URL
 */
export const getDrivePreviewUrl = (fileIdOrUrl: string): string | null => {
  const fileId = extractDriveFileId(fileIdOrUrl) || fileIdOrUrl;
  if (!fileId) return null;
  
  return `https://drive.google.com/file/d/${fileId}/preview`;
};

/**
 * Get direct Google Drive view URL
 */
export const getDriveViewUrl = (fileIdOrUrl: string): string | null => {
  const fileId = extractDriveFileId(fileIdOrUrl) || fileIdOrUrl;
  if (!fileId) return null;
  
  return `https://drive.google.com/file/d/${fileId}/view`;
};

/**
 * Process image URL for optimal display
 * Returns thumbnail for grid view, full resolution for detail view
 */
export const getOptimalImageUrl = (url: string, mode: 'thumbnail' | 'full' = 'full'): string => {
  const isDrive = url.includes('drive.google.com') || url.includes('gdrive::');
  
  if (!isDrive) return url;
  
  const { fileId, thumbnail } = parseGDriveUrl(url);
  if (!fileId) return url;
  
  if (mode === 'thumbnail' && thumbnail) {
    return thumbnail;
  }
  
  return getDriveStreamUrl(fileId) || url;
};
